import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { Employee } from "@/types/employee";
import { useUser } from "@/context/UserContext";

interface BasicInfoFormProps {
  employee: Employee | null;
  onUpdate: (updated: Employee) => void;
  onToast: (msg: string, type?: "success" | "error") => void;
}

export default function BasicInfoForm({ employee, onUpdate, onToast }: BasicInfoFormProps) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    id_card_number: "",
  });
  const [saving, setSaving] = useState(false);
  const { refreshProfileComplete } = useUser();

  useEffect(() => {
    if (employee) {
      setForm({
        name: employee.name || "",
        email: employee.email || "",
        phone: employee.phone || "",
        address: employee.address || "",
        id_card_number: employee.id_card_number || "",
      });
    }
  }, [employee]);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!employee?.id) return;
    setSaving(true);

    const { error } = await supabase
      .from("employees")
      .update({
        name: form.name,
        phone: form.phone || null,
        address: form.address || null,
        id_card_number: form.id_card_number || null,
      })
      .eq("id", employee.id);

    if (error) {
      onToast("儲存失敗：" + error.message, "error");
    } else {
      onUpdate({ ...employee, ...form });
      onToast("基本資料已儲存", "success");
      refreshProfileComplete();
    }
    setSaving(false);
  };

  const fieldClass =
    "w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 bg-background-50";

  return (
    <div className="bg-background-50 border border-background-200 rounded-xl p-5 md:p-6">
      <h2 className="text-base font-bold text-foreground-900 mb-4">基本資料</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-foreground-600 mb-1.5">
            姓名 <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className={fieldClass}
            placeholder="請輸入姓名"
          />
        </div>
        <div>
          <label className="block text-xs text-foreground-600 mb-1.5">Email</label>
          <input
            type="email"
            value={form.email}
            disabled
            className={`${fieldClass} opacity-60 cursor-not-allowed`}
          />
        </div>
        <div>
          <label className="block text-xs text-foreground-600 mb-1.5">
            電話 <span className="text-accent-500">*</span>
          </label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            className={fieldClass}
            placeholder="請輸入電話"
          />
        </div>
        <div>
          <label className="block text-xs text-foreground-600 mb-1.5">
            地址 <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            value={form.address}
            onChange={(e) => handleChange("address", e.target.value)}
            className={fieldClass}
            placeholder="請輸入地址"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-foreground-600 mb-1.5">
            身分證字號 <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            value={form.id_card_number}
            onChange={(e) => handleChange("id_card_number", e.target.value)}
            className={`${fieldClass} md:w-1/2`}
            placeholder="請輸入身分證字號"
          />
        </div>
      </div>
      <div className="flex justify-end mt-5">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
        >
          {saving ? "儲存中..." : "儲存"}
        </button>
      </div>
    </div>
  );
}